import React, { useState } from 'react';
import { 
  ArrowLeft, Search, Plus, Tag, Edit, Trash2, MoreVertical,
  Car, Fuel, Wrench, ShoppingBag, Coffee, Building2, Briefcase,
  Phone, Globe, Zap, Droplets, Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const ExpenseCategories = ({ onBack }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 1, name: 'Fuel', icon: Fuel, color: '#f59e0b', count: 24, total: 601.53 },
    { id: 2, name: 'Equipment', icon: Wrench, color: '#3b82f6', count: 8, total: 245.00 },
    { id: 3, name: 'Vehicle Maintenance', icon: Car, color: '#10b981', count: 5, total: 312.85 },
    { id: 4, name: 'Supplies', icon: ShoppingBag, color: '#8b5cf6', count: 12, total: 156.48 },
    { id: 5, name: 'Food & Drink', icon: Coffee, color: '#ec4899', count: 18, total: 87.45 },
    { id: 6, name: 'Office', icon: Building2, color: '#6366f1', count: 3, total: 124.99 },
    { id: 7, name: 'Professional Services', icon: Briefcase, color: '#0891b2', count: 2, total: 450.00 },
    { id: 8, name: 'Phone & Internet', icon: Phone, color: '#84cc16', count: 4, total: 189.96 },
    { id: 9, name: 'Software & Subscriptions', icon: Globe, color: '#f97316', count: 6, total: 234.00 },
    { id: 10, name: 'Utilities', icon: Zap, color: '#14b8a6', count: 3, total: 312.00 },
    { id: 11, name: 'Water (Pure Water)', icon: Droplets, color: '#06b6d4', count: 7, total: 175.00 },
    { id: 12, name: 'Insurance', icon: Shield, color: '#d946ef', count: 1, total: 125.00 },
  ];

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalExpenses = categories.reduce((sum, cat) => sum + cat.total, 0);

  return (
    <div className="h-full flex flex-col bg-gray-50" data-testid="expense-categories-page">
      {/* Header */}
      <div className="bg-zinc-800 text-white px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBack && (
              <button onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="exp-cat-back-btn">
                <ArrowLeft size={20} />
              </button>
            )}
            <h1 className="text-xl font-semibold">Expense Categories</h1>
          </div>
          <button 
            className="flex items-center gap-2 bg-pink-500 hover:bg-pink-600 px-4 py-2 rounded-lg transition-colors"
            data-testid="add-category-btn"
          >
            <Plus size={18} />
            Add Category
          </button>
        </div>

        {/* Summary */}
        <div className="mt-4 flex items-center justify-between bg-white/10 rounded-lg p-4">
          <div>
            <p className="text-white/70 text-sm">Total Categories</p>
            <p className="text-2xl font-bold">{categories.length}</p>
          </div>
          <div className="text-right">
            <p className="text-white/70 text-sm">Total Expenses</p>
            <p className="text-2xl font-bold">£{totalExpenses.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="p-4 bg-white border-b border-gray-200">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
            data-testid="category-search"
          />
        </div>
      </div>

      {/* Categories List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {filteredCategories.map(category => {
          const Icon = category.icon;
          return (
            <div 
              key={category.id}
              className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-all"
              data-testid={`category-${category.id}`}
            >
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  <Icon size={24} style={{ color: category.color }} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{category.name}</p>
                  <p className="text-sm text-gray-500">{category.count} expenses</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">£{category.total.toFixed(2)}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Edit size={16} className="text-gray-400" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Trash2 size={16} className="text-gray-400" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <Tag size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No categories found</p>
          </div>
        )}
      </div>

      {/* FAB - Fixed bottom LEFT */}
      <button 
        className="fixed bottom-6 left-6 w-14 h-14 bg-pink-500 hover:bg-pink-600 rounded-full shadow-lg flex items-center justify-center transition-colors z-50"
        data-testid="fab-add-category"
      >
        <Plus size={24} className="text-white" />
      </button>
    </div>
  );
};

export default ExpenseCategories;
